import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, Input, NgZone, OnInit, Output, TemplateRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Sort, SortDirection } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { LibraryItem } from '../../models';
import { BaseSettingsGetService } from '../../../common/services';
import { SETTINGS_SERVICE } from '../../../common/constants';
import { CdkDrag, CdkDragDrop, CdkDragSortEvent, CdkDropList } from '@angular/cdk/drag-drop';
import { LibraryType } from '../../../models';

@Component({
    selector: 'lib-library-list-table',
    templateUrl: './library-list-table.component.html',
    styleUrls: ['./library-list-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LibraryListTableComponent implements OnInit {

  @ViewChild(MatTable) table: MatTable<any>;
  @Input() public set items(value: LibraryItem[]) {
    this._items = value || [];

    this.dataSource = new MatTableDataSource(this._items);
  }

  public get items() {
    return this._items;
  }

  @Input() public enablePinned = false;
  @Input() public canReorder = false;
  @Input() contextTemplate: TemplateRef<any>;
  @Input() public defaultSort: string;
  @Input() public additionalActionsTemplate?: TemplateRef<any>;
  @Input() dropListConnectedTo: string[];

  @Output() public sortChange: EventEmitter<Sort> = new EventEmitter<Sort>();
  @Output() public open: EventEmitter<LibraryItem> = new EventEmitter<LibraryItem>();
  @Output() public reorder: EventEmitter<{dragDrop: CdkDragDrop<any>, folderReorder: boolean}> = new EventEmitter();

  public startSort: Sort = null;

  public minifiedView = false;
  public hideUpdatedDate = false;
  public folderReorder = false;

  public dataSource: MatTableDataSource<LibraryItem> = new MatTableDataSource([]);

  public readonly displayedColumns = ['title', 'tags', 'updated', 'fileSize', 'actions'];

  private folderDragOverTimeout;
  private readonly minWidth = 800;
  private readonly FOLDER_REORDER_TIME_MS = 750;
  private readonly mediaQuery = window.matchMedia('(max-width: 992px)');

  private _items: LibraryItem[] = [];

  constructor(
    private readonly element: ElementRef,
    private readonly cdr: ChangeDetectorRef,
    private readonly ngZone: NgZone,
    @Inject(SETTINGS_SERVICE) private readonly settingsService: BaseSettingsGetService,
  ) { }

  ngOnInit(): void {
    const [active, direction] = this.defaultSort ? this.defaultSort.split(':') : [];
    this.startSort = this.defaultSort ? {
      active,
      direction: direction.toLocaleLowerCase() as SortDirection
    }: null;

    this.detectIfViewShouldBeMinified();

    this.settingsService.getSettings().subscribe(settings => {
      this.hideUpdatedDate = settings.library?.removeUpdatedDateFromCard;
      if (this.hideUpdatedDate) {
        const colIndex = this.displayedColumns.findIndex(col => col === 'updated');
        this.displayedColumns.splice(colIndex, 1);
      }
    })
  }

  public onSortChange(sort: Sort) {
    this.sortChange.emit(sort);
  }

  public openLibraryItem(item: LibraryItem): void {
    this.open.emit(item);
  }

  public stopPropagation(event: Event) {
    event.stopPropagation();
    event.preventDefault();
  }

  public drop(event: CdkDragDrop<any>): void {
    this.reorder.emit({ dragDrop: event, folderReorder: this.folderReorder });

    setTimeout(() => {
      this.table.renderRows();
    });
  }

  public sortPredicate(index: number, drag: CdkDrag, drop: CdkDropList): boolean {
    return drag.data.type !== LibraryType.FOLDER || drop.data[index]?.type  !== LibraryType.FILE;
  }

  public onDragEntered(drag: CdkDragSortEvent<LibraryItem[], any>): void {
    this.clearFolderDragOverTimeout();

    if (drag.item.data?.type !== LibraryType.FOLDER) {
      return;
    }

    this.ngZone.run(() => {
      this.folderReorder = true;
      this.cdr.markForCheck();
    });

    this.folderDragOverTimeout = setTimeout(() => {
    this.ngZone.run(() => {
      this.folderReorder = false;
      this.cdr.markForCheck();
    });
    }, this.FOLDER_REORDER_TIME_MS);
  }

  private clearFolderDragOverTimeout(): void {
    clearTimeout(this.folderDragOverTimeout);
    this.ngZone.run(() => {
      this.folderReorder = false;
      this.cdr.markForCheck();
    });
  }

  private detectIfViewShouldBeMinified(): void {
    if (!this.element.nativeElement) {
      return;
    }

    const observer = new ResizeObserver(() => {
      this.minifiedView = this.element.nativeElement.offsetWidth < this.minWidth || this.mediaQuery.matches;
    });

    observer.observe(window.document.body);

    this.minifiedView = this.element.nativeElement.offsetWidth < this.minWidth || this.mediaQuery.matches;
  }

}
